import { Link } from "react-router-dom"
import { useContext, useState } from "react"
import _ from "lodash"
import { Calendar } from "primereact/calendar"
import { SplitButton } from "primereact/splitbutton"
import { useNavigate } from "react-router-dom"
import { useQuery } from "@apollo/client"

// Components
import ParticipantSidebar from "../../components/sidebars/ParticipantSidebar"
import PageSidebarContent from "../../components/sidebars/PageSidebarContent"
import CustomTemplateName from "../../components/CustomTemplateName"
import LoaderMedium from "../../components/Loaders"
import GenericTable from "../../components/DataTable/GenericTable"
import FilterHeader from "../../components/DataTable/FilterHeader"

// Context
import { UserContext } from "../../context/userContext"

// Helpers
import { parseDate } from "../../helpers/dateNormalization"
import dataParser from "../../helpers/parseJsonData"
import {
  formatDate,
  handleOptsForFilter,
  formatLength,
} from "../../components/DataTable/helpers"
import styles from "../../styles/CustomDataForms.module.css"

// Queries
import { CUSTOM_FORM_BY_PARTICIPANT } from "./queries"
import GLOBALS_STATUS from "../../config/globalsEngagementsStatus"

// Hooks
import { useFilters } from "../../components/Filters/useFilters"
import useLabels from "../../hooks/useLabels"
import { Button } from "primereact/button"
import { navigateWithOrigin } from "../../helpers/navigation"

export default function HousingAssistance() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [dateRange, setDateRange] = useState(null)
  const [loadingLabels, labels] = useLabels(["housing_assistance_grant_award"])

  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const ID_TEMPLATE = "housing_assistance_grant_award"

  const currentYear = new Date().getFullYear()
  const yearRange = `2000:${currentYear + 10}`

  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    onFilterChange,
    clearGlobalFilter,
    clearAllFilters,
  } = useFilters()

  useQuery(CUSTOM_FORM_BY_PARTICIPANT, {
    variables: {
      participant_id: userCtx.participant_id,
      organization_id: userCtx.session.user.organization_id,
      participant_membership_id: userCtx.participant_membership_id,
      idTemplate: ID_TEMPLATE,
    },
    skip: !userCtx,
    fetchPolicy: "no-cache",
    onCompleted: async (data) => {
      if (data.customForm === null) {
        setData(null)
        return setLoading(false)
      }

      const dataFormatted = dataParser(data.customForm.data)
      const handleActionButtons = (row) => {
        return _.includes(
          GLOBALS_STATUS.ACTIVE_STATUS,
          userCtx.participant_membership_status
        ) ? (
          <SplitButton
            label="View"
            to={`/form/${ID_TEMPLATE}/1/${row.idStamp}`}
            onClick={() =>
              navigateWithOrigin(
                navigate,
                `/form/view/${row.idTemplate}/${row.idStamp}`
              )
            }
            model={[
              {
                label: "Edit",
                command: () =>
                  navigate(`/form/${row.idTemplate}/1/${row.idStamp}`),
              },
            ]}
          />
        ) : (
          <Button
            label="View"
            to={`/form/${ID_TEMPLATE}/1/${row.idStamp}`}
            onClick={() =>
              navigateWithOrigin(
                navigate,
                `/form/view/${row.idTemplate}/${row.idStamp}`
              )
            }
          />
        )
      }
      const handleLocations = (row) => {
        const locationsList = _.map(row, ({ label }) => label)
        return _.join(locationsList, ", ")
      }

      const formattedData = dataFormatted.map((row) => {
        return {
          date: parseDate(row.session_date || row.form_date),
          facilitatingStaff: row.staff_full_name,
          locations:
            handleLocations(row.session__location_ids) ||
            handleLocations(row.form__location_ids),
          channel: row.session_channel || row.form_channel,
          form_length: row.session_length || row.form_length,
          edit: handleActionButtons(row),
        }
      })

      setData(formattedData)
      setLoading(false)
    },
  })

  if (loading || loadingLabels) return <LoaderMedium />

  const uniqueLocations = handleOptsForFilter(data, "locations")
  const uniqueFacilitatingStaff = handleOptsForFilter(data, "facilitatingStaff")

  const filterConfig = [
    {
      label: "Date",
      value: dateRange,
      options: null,
      onChange: (e) => {
        setDateRange(e.value)
        onFilterChange("date", e.value)
      },
      onClear: () => {
        setDateRange(null)
        onFilterChange("date", null)
      },
      enableFilter: true,
      component: (
        <Calendar
          value={dateRange}
          onChange={(e) => {
            setDateRange(e.value)
            onFilterChange("date", e.value)
          }}
          placeholder="Select Date Range"
          selectionMode="range"
          dateFormat="mm/dd/yy"
          readOnlyInput
          numberOfMonths={2}
          showIcon
          showButtonBar
          yearNavigator
          yearRange={yearRange}
        />
      ),
    },
    {
      label: "Locations",
      value: filters.locations?.value || "",
      options: uniqueLocations,
      onChange: (e) => onFilterChange("locations", e.value),
      onClear: () => onFilterChange("locations", null),
      enableFilter: true,
    },
    {
      label: "Facilitating Staff",
      value: filters.facilitatingStaff?.value || "",
      options: uniqueFacilitatingStaff,
      onChange: (e) => onFilterChange("facilitatingStaff", e.value),
      onClear: () => onFilterChange("facilitatingStaff", null),
      enableFilter: true,
    },
  ]

  const columns = [
    {
      field: "date",
      header: "Date",
      sortable: true,
      body: (rowData) => formatDate(rowData.date),
    },
    { field: "locations", header: "Location", sortable: true },

    {
      field: "facilitatingStaff",
      header: "Facilitating Staff",
      sortable: true,
    },
    { field: "channel", header: "Engagement Channel" },
    {
      field: "form_length",
      header: "Session Length",
      body: (rowData) => formatLength(rowData.form_length),
    },
    { field: "edit", header: "Action" },
  ]

  const handleClearAllFilters = () => {
    clearAllFilters()
  }

  return (
    <>
      <PageSidebarContent>
        <ParticipantSidebar />
      </PageSidebarContent>
      <div className="main-content">
        <br />
        <h1 className={styles.title}>
          <CustomTemplateName
            parent="housing_assistance_grant_award"
            original_label="Housing Assistance Grant Award Form"
          />
        </h1>

        {_.includes(
          GLOBALS_STATUS.ACTIVE_STATUS,
          userCtx.participant_membership_status
        ) && (
          <Link to={`/form/${ID_TEMPLATE}/1/`} className="btn">
            New {labels.housing_assistance_grant_award}
          </Link>
        )}
        <br />
        <br />
        <GenericTable
          data={data}
          columns={columns}
          filters={filters}
          header={
            <FilterHeader
              filters={filterConfig}
              globalFilterValue={globalFilterValue}
              onGlobalFilterChange={onGlobalFilterChange}
              clearGlobalFilter={clearGlobalFilter}
              clearAllFilters={handleClearAllFilters}
            />
          }
          paginator
          rows={50}
        />
      </div>
    </>
  )
}
